// Мастер «Новый заказчик» и договор с ним.
//
// Тз, раздел 5.2, называет среди экранов «Реестр заказчиков и карточка заказчика» и «Карточку
// договора»; сценарий приёмки 11 — «Заведение заказчика и договора». Двери на сервере были обе,
// а экрана не было ни одного: на чистой системе заказчика нельзя было завести вовсе. И это не
// теоретическая дыра — объект без заказчика не заводится, так что чистая система упиралась в
// тупик на первом же шаге. Пустой список заказчиков честно писал «Заказчик нужен объекту при
// создании» и не предлагал ни одного способа его завести.
//
// ## Договор заводится тем же заходом, а не отдельным
//
// У заказчика без договора не к чему привязать объект: договор несёт юрлицо-исполнителя и
// перечень объектов. Разводить их по двум мастерам значит гарантировать, что половина
// заказчиков останется без договора, — а обнаружится это при заведении объекта, через неделю.
// Поэтому договор здесь необязательная вторая половина одной формы: не заполнили — заказчик
// всё равно заведётся, и об этом сказано прямо.

import { useState, type FormEvent } from 'react';

import { RiCheckboxCircleLine, RiErrorWarningLine } from '@remixicon/react';

import type { ApiError } from '@/shared/errors/api-error';
import { newIdempotencyKey } from '@/shared/api/idempotency';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { DateField } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';
import {
  useCreateContractMutation,
  useCreateCustomerMutation,
  useLegalEntitiesQuery,
} from '@/shared/org/queries';

/** Заведение заказчика — право на правку справочников, как и у объекта. */
const EDIT_PERMISSION = 'core.dictionary.edit';

export function NewCustomerPanel({ onClose }: { onClose: () => void }) {
  const права = useAuthStore((state) => state.permissions);
  const можно = can(права, EDIT_PERMISSION);
  const юрлица = useLegalEntitiesQuery();

  const [ключ, setКлюч] = useState(newIdempotencyKey);
  const [название, setНазвание] = useState('');
  const [инн, setИнн] = useState('');
  const [отрасль, setОтрасль] = useState('');
  const [регион, setРегион] = useState('');
  const [контакт, setКонтакт] = useState('');
  const [телефон, setТелефон] = useState('');

  const [юрлицо, setЮрлицо] = useState(NO_VALUE);
  const [номер, setНомер] = useState('');
  const [подписан, setПодписан] = useState('');

  const [ошибки, setОшибки] = useState<Record<string, string>>({});
  const [запрет, setЗапрет] = useState<string | null>(null);
  const [заведён, setЗаведён] = useState<{ имя: string; сДоговором: boolean } | null>(null);

  const заказчик = useCreateCustomerMutation(ключ);
  const договор = useCreateContractMutation(ключ);

  const заводимДоговор = юрлицо !== NO_VALUE || номер.trim() !== '' || подписан !== '';

  async function отправить(событие: FormEvent<HTMLFormElement>) {
    событие.preventDefault();
    if (!можно) {
      setЗапрет(
        'Заводит заказчиков тот, у кого есть право на правку справочников. Попросите администратора выдать его.',
      );
      return;
    }
    const next: Record<string, string> = {};
    if (название.trim() === '') {
      next.name = 'Название обязательно: по нему заказчика ищут и в реестре, и в отчётах.';
    }
    // Договор — половина необязательная, но начатая наполовину не бывает: без юрлица
    // непонятно, кто исполнитель, и объект к такому договору не привяжется.
    if (заводимДоговор && юрлицо === NO_VALUE) {
      next.legal_entity_id =
        'Для договора нужно юрлицо-исполнитель: от него зависит, в чью ведомость попадут люди объекта.';
    }
    if (Object.keys(next).length > 0) {
      setОшибки(next);
      return;
    }

    setОшибки({});
    setЗапрет(null);

    const созданный = await заказчик.mutateAsync({
      name: название.trim(),
      inn: инн.trim() === '' ? null : инн.trim(),
      industry: отрасль.trim() === '' ? null : отрасль.trim(),
      region: регион.trim() === '' ? null : регион.trim(),
      contact_person: контакт.trim() === '' ? null : контакт.trim(),
      contact_phone: телефон.trim() === '' ? null : телефон.trim(),
    });

    if (заводимДоговор) {
      await договор.mutateAsync({
        customer_id: созданный.id,
        legal_entity_id: юрлицо,
        number: номер.trim() === '' ? null : номер.trim(),
        signed_at: подписан === '' ? null : подписан,
      });
    }

    // Ключ идемпотентности живёт столько же, сколько форма: следующий заказчик — новая форма.
    setКлюч(newIdempotencyKey());
    setЗаведён({ имя: созданный.name, сДоговором: заводимДоговор });
  }

  if (заведён !== null) {
    return (
      <div className='flex flex-col gap-3'>
        <Alert.Root variant='light' status='success' size='small' role='status'>
          <Alert.Icon as={RiCheckboxCircleLine} />
          <span>
            Заказчик «{заведён.имя}» заведён
            {заведён.сДоговором ? ' вместе с договором.' : ' без договора.'}
          </span>
        </Alert.Root>
        {!заведён.сДоговором && (
          // Не «готово», а что дальше: объект привязывается к договору, и без него следующий
          // шаг упрётся в то же место, где человек стоял минуту назад.
          <p className='text-paragraph-sm text-text-sub-600'>
            Договора у него нет. Объект привязывается к договору — заведите его, прежде чем заводить
            объекты этого заказчика.
          </p>
        )}
        <div className='flex items-center gap-2 pt-1'>
          <Button.Root type='button' variant='primary' mode='filled' size='small' onClick={onClose}>
            Готово
          </Button.Root>
          <Button.Root
            type='button'
            variant='neutral'
            mode='stroke'
            size='small'
            onClick={() => {
              setЗаведён(null);
              setНазвание('');
              setИнн('');
              setОтрасль('');
              setРегион('');
              setКонтакт('');
              setТелефон('');
              setЮрлицо(NO_VALUE);
              setНомер('');
              setПодписан('');
            }}
          >
            Завести ещё одного
          </Button.Root>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={отправить} noValidate className='flex flex-col gap-3'>
      {!можно && (
        <Alert.Root variant='light' status='warning' size='small' role='status'>
          <Alert.Icon as={RiErrorWarningLine} />
          <span>
            Права на правку справочников у вас нет: форму можно заполнить, но заказчик не
            сохранится. Право выдаёт администратор.
          </span>
        </Alert.Root>
      )}

      <TextField
        id='customer-name'
        label='Название'
        required
        value={название}
        onChange={setНазвание}
        placeholder='ООО «АвтоБаза»'
        error={ошибки.name}
      />
      <TextField
        id='customer-inn'
        label='ИНН'
        value={инн}
        onChange={setИнн}
        placeholder='7701234567'
      />
      <TextField
        id='customer-industry'
        label='Отрасль'
        value={отрасль}
        onChange={setОтрасль}
        placeholder='нефтегаз, торговый центр, застройщик'
      />
      <TextField
        id='customer-region'
        label='Регион'
        value={регион}
        onChange={setРегион}
        placeholder='ХМАО'
      />
      <TextField
        id='customer-contact'
        label='Контактное лицо'
        value={контакт}
        onChange={setКонтакт}
        placeholder='Иванов Пётр'
      />
      <TextField
        id='customer-phone'
        label='Телефон'
        value={телефон}
        onChange={setТелефон}
        placeholder='+7 900 123-45-67'
      />

      <div className='flex flex-col gap-3 rounded-lg bg-bg-weak-50 p-3'>
        <span className='text-label-sm text-text-strong-950'>Договор с ним</span>
        <p className='text-paragraph-xs text-text-sub-600'>
          Можно не заполнять сейчас. Но объект привязывается к договору: без него объекты этого
          заказчика завести не выйдет.
        </p>
        <SelectField
          id='contract-legal-entity'
          label='Юрлицо-исполнитель'
          value={юрлицо}
          onChange={setЮрлицо}
          noneLabel='пока без договора'
          options={(юрлица.data?.items ?? []).map((строка) => ({
            value: строка.id,
            label: строка.short_name ?? строка.name,
          }))}
          placeholder='выберите юрлицо'
          pending={юрлица.isPending}
          failed={юрлица.isError}
          failedError={юрлица.error as ApiError | null}
          emptyNote='Юрлиц в справочнике нет. Их заводит администратор в настройках системы.'
          error={ошибки.legal_entity_id}
        />
        <TextField
          id='contract-number'
          label='Номер договора'
          value={номер}
          onChange={setНомер}
          placeholder='12/2026'
        />
        <DateField id='contract-signed' label='Подписан' value={подписан} onChange={setПодписан} />
      </div>

      {заказчик.isError && <ErrorAlert error={заказчик.error} />}
      {договор.isError && <ErrorAlert error={договор.error} />}
      {запрет !== null && (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {запрет}
        </Hint.Root>
      )}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {заказчик.isPending || договор.isPending ? 'Заводим…' : 'Завести заказчика'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}
